import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { blink } from '@/lib/blink'; // Use shared SDK instance
import { authCache } from '@/lib/authCache'; // Auth caching service
import AppLayout from '@/components/layout/AppLayout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import { usePerformanceMonitor, setupUnloadTelemetry } from './lib/performanceMonitor';
import { timelineAggregationService } from '@/services/timelineAggregationService';
import EmailInbox from '@/pages/EmailInbox';
import AIChat from '@/pages/AIChat';
import Workflows from '@/pages/Workflows';
import EmailComposer from '@/pages/EmailComposer';
import Settings from '@/pages/Settings';
import CalendarPage from '@/pages/CalendarPage';
import UserProfile from '@/pages/UserProfile';
import OAuthCallback from '@/pages/OAuthCallback';
import ContactIntelligence from '@/pages/ContactIntelligence';
import Top50Dashboard from '@/pages/Top50Dashboard';
import CIScoringValidation from '@/pages/CIScoringValidation';
import TelemetryDashboard from '@/pages/TelemetryDashboard';
import EmailSyncMonitor from '@/pages/EmailSyncMonitor';
import StructuredAIChat from '@/pages/StructuredAIChat';
import LiveAPITestDemo from '@/pages/LiveAPITestDemo';
import LiveGraphAPITest from '@/pages/LiveGraphAPITest';
import LiveGraphAPIMonitor from '@/pages/LiveGraphAPIMonitor';
import ComprehensiveLiveTest from '@/pages/ComprehensiveLiveTest';
import LiveTestSuite from '@/pages/LiveTestSuite';
import ApiTestSuite from '@/pages/ApiTestSuite';
import ComprehensiveGraphAPITest from '@/pages/ComprehensiveGraphAPITest';
import LiveUserPromptTester from '@/pages/LiveUserPromptTester';
import MultiAgentTest from '@/pages/MultiAgentTest';
import LiveOAuthTestDashboard from '@/pages/LiveOAuthTestDashboard';
import AutonomousAgentDemo from '@/pages/AutonomousAgentDemo';
import AgentTraceDashboard from '@/pages/AgentTraceDashboard';
import TasksPage from '@/pages/TasksPage';
import SchemaValidationTest from '@/pages/SchemaValidationTest';
import WorkflowTestSuite from '@/pages/WorkflowTestSuite';
import NodeTestDashboard from '@/pages/NodeTestDashboard';
import AICallTestSuite from '@/pages/AICallTestSuite';
import UIMonitoringDashboard from '@/pages/UIMonitoringDashboard';
import WorkflowNodeTester from '@/pages/WorkflowNodeTester';
import RelationshipGlassCardDemo from '@/pages/RelationshipGlassCardDemo';
import DevLogsDashboard from '@/pages/DevLogsDashboard';
import TimelinePage from '@/pages/TimelinePage';

interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  isTestMode?: boolean;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Enable performance monitoring
  usePerformanceMonitor(60000); // Log report every 60 seconds
  
  useEffect(() => {
    setupUnloadTelemetry();
  }, []);

  // Start timeline aggregation service only when user is authenticated
  useEffect(() => {
    if (user?.id) {
      console.log('✅ Starting timeline aggregation service for authenticated user');
      timelineAggregationService.start(120000); // Sync every 2 minutes
    } else {
      console.log('⚠️ Stopping timeline aggregation service - no authenticated user');
      timelineAggregationService.stop();
    }
    
    return () => {
      timelineAggregationService.stop();
    };
  }, [user?.id]);

  useEffect(() => {
    // Try to load from cache first for instant auth state
    const cachedUser = authCache.getCachedUser();
    if (cachedUser) {
      console.log('⚡ Loading user from cache (instant, no API call)');
      setUser({
        id: cachedUser.id,
        email: cachedUser.email || '',
        name: cachedUser.displayName,
        avatar: cachedUser.avatarUrl,
        isTestMode: false
      });
      setAuthInitialized(true);
      setLoading(false);
      
      // Stats for debugging
      const stats = authCache.getStats();
      console.log('📊 Auth cache stats:', stats);
    }

    // Set a timeout to ensure we don't get stuck loading
    const loadingTimeout = setTimeout(() => {
      if (!authInitialized) {
        console.warn('⚠️ Auth initialization timeout - proceeding with guest mode');
        setAuthInitialized(true);
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    // Check for real authentication only
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      console.log('🔐 Auth state changed:', { 
        hasUser: !!state.user, 
        isLoading: state.isLoading,
        userId: state.user?.id 
      });
      
      if (state.user) {
        console.log('✅ User authenticated:', state.user.email);
        const userData = {
          id: state.user.id,
          email: state.user.email || '',
          name: state.user.displayName,
          avatar: state.user.avatarUrl,
          isTestMode: false
        };
        
        setUser(userData);
        
        // Cache the user for 5 minutes
        authCache.setCachedUser({
          id: state.user.id,
          email: state.user.email || '',
          displayName: state.user.displayName,
          avatarUrl: state.user.avatarUrl
        }, 5 * 60 * 1000); // 5 minute cache
        
        setAuthInitialized(true);
        setLoading(false); // Force loading to false when user is authenticated
      } else {
        console.log('❌ No user authenticated');
        setUser(null);
        authCache.invalidate(); // Clear cache on logout
        setAuthInitialized(true);
        setLoading(state.isLoading);
      }
    });

    // Listen for OAuth errors and show notifications
    const handleOAuthRefreshFailed = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { provider, error } = customEvent.detail;
      toast.error(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} Authentication Failed`, {
        description: error || 'Failed to refresh access token. Please reconnect your account.',
        action: {
          label: 'Reconnect',
          onClick: () => window.location.href = '/settings'
        }
      });
    };

    const handleOAuthTokenExpired = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { provider, message } = customEvent.detail;
      toast.warning(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} Token Expired`, {
        description: message || 'Your session has expired. Please reconnect your account.',
        action: {
          label: 'Go to Settings',
          onClick: () => window.location.href = '/settings'
        }
      });
    };

    window.addEventListener('oauth-refresh-failed', handleOAuthRefreshFailed);
    window.addEventListener('oauth-token-expired', handleOAuthTokenExpired);

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
      window.removeEventListener('oauth-refresh-failed', handleOAuthRefreshFailed);
      window.removeEventListener('oauth-token-expired', handleOAuthTokenExpired);
    };
  }, [authInitialized]);

  const handleLogout = () => {
    // Logout from real authentication
    console.log('🚪 Logging out - invalidating auth cache');
    authCache.invalidate(); // Clear cache immediately
    blink.auth.logout();
    setUser(null);
    window.location.href = '/login';
  };

  if (loading && authInitialized === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background gradient-mesh">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl gradient-primary mx-auto animate-pulse-slow" />
          <p className="text-muted-foreground">Loading Naible-Thread...</p>
          <p className="text-xs text-muted-foreground/60">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/AUTH/GOOGLE/CALLBACK" element={<OAuthCallback />} />
          <Route path="/auth/google/callback" element={<OAuthCallback />} />
          <Route
            path="/"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <Dashboard />
              </AppLayout>
            }
          />
          <Route
            path="/inbox"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <EmailInbox />
              </AppLayout>
            }
          />
          <Route
            path="/ai-chat"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <AIChat />
              </AppLayout>
            }
          />
          <Route
            path="/ai-structured"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <StructuredAIChat userId={user.id} />
              </AppLayout>
            }
          />
          <Route
            path="/workflows"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <Workflows />
              </AppLayout>
            }
          />
          <Route
            path="/compose"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <EmailComposer />
              </AppLayout>
            }
          />
          <Route
            path="/calendar"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <CalendarPage />
              </AppLayout>
            }
          />
          <Route
            path="/settings"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <Settings />
              </AppLayout>
            }
          />
          <Route
            path="/profile"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <UserProfile />
              </AppLayout>
            }
          />
          <Route
            path="/contacts"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <ContactIntelligence />
              </AppLayout>
            }
          />
          <Route
            path="/top-50"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <Top50Dashboard />
              </AppLayout>
            }
          />
          <Route
            path="/ci-validation"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <CIScoringValidation />
              </AppLayout>
            }
          />
          <Route
            path="/telemetry"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <TelemetryDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/email-sync-monitor"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <EmailSyncMonitor />
              </AppLayout>
            }
          />
          <Route
            path="/live-api-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveAPITestDemo />
              </AppLayout>
            }
          />
          <Route
            path="/graph-api-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveGraphAPITest />
              </AppLayout>
            }
          />

          <Route
            path="/graph-api-monitor"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveGraphAPIMonitor />
              </AppLayout>
            }
          />
          <Route
            path="/comprehensive-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <ComprehensiveLiveTest />
              </AppLayout>
            }
          />
          <Route
            path="/test-suite"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveTestSuite />
              </AppLayout>
            }
          />
          <Route
            path="/api-test-suite"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <ApiTestSuite />
              </AppLayout>
            }
          />
          <Route
            path="/comprehensive-graph-api-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <ComprehensiveGraphAPITest />
              </AppLayout>
            }
          />
          <Route
            path="/test-user-prompts"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveUserPromptTester />
              </AppLayout>
            }
          />
          <Route
            path="/multi-agent-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <MultiAgentTest />
              </AppLayout>
            }
          />
          <Route
            path="/live-oauth-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveOAuthTestDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/autonomous-agent"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <AutonomousAgentDemo />
              </AppLayout>
            }
          />
          <Route
            path="/agent-trace-dashboard"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <AgentTraceDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/live-email-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <LiveTestSuite />
              </AppLayout>
            }
          />
          <Route
            path="/tasks"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <TasksPage />
              </AppLayout>
            }
          />
          <Route
            path="/schema-validation-test"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <SchemaValidationTest />
              </AppLayout>
            }
          />
          <Route
            path="/workflow-test-suite"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <WorkflowTestSuite />
              </AppLayout>
            }
          />
          <Route
            path="/node-tests"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <NodeTestDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/test-ai-calls"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <AICallTestSuite />
              </AppLayout>
            }
          />
          <Route
            path="/ui-monitoring"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <UIMonitoringDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/workflow-node-tester"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <WorkflowNodeTester />
              </AppLayout>
            }
          />
          <Route
            path="/relationship-glass-demo"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <RelationshipGlassCardDemo />
              </AppLayout>
            }
          />
          <Route
            path="/dev-logs"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <DevLogsDashboard />
              </AppLayout>
            }
          />
          <Route
            path="/timeline"
            element={
              <AppLayout user={user} onLogout={handleLogout}>
                <TimelinePage />
              </AppLayout>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}